/**
 * The golden round's open numeric answer (change 6).
 *
 * The form and the reasoning behind it live in `../lib/numeric.ts`; this is the
 * twelve buttons around that logic. Three things about the layout are load
 * bearing rather than decorative:
 *
 *  1. NO TEXT FIELD, and therefore no software keyboard. The value is built by
 *     tapping digits. Nothing here can raise an OS keyboard, so the viewport
 *     never halves under a running clock.
 *  2. THE KEYS NEVER MOVE. Illegal digits grey out but keep their place, so
 *     muscle memory from the phone dialler transfers and the thumb is not
 *     re-hunting after every tap.
 *  3. COMMIT IS ITS OWN FULL-WIDTH BAR, at the bottom, physically separated
 *     from the digits. There is no auto-submit on a full value: the last digit
 *     of a year is exactly where a mis-tap is most likely, and a pad that fired
 *     on it would turn a slip into a busted golden round.
 *  4. NOTHING IS TYPED FOR THE PLAYER. Where the bounds leave exactly one legal
 *     next digit, that key LIGHTS — it does not press itself. See the note on
 *     `forcedDigit` for what that cost when the pad pressed it instead.
 *
 * The display is digit SLOTS rather than a number, so a half-typed year reads
 * as `19 6 _` — the player can see how much is left without counting.
 */

import { useEffect, useState } from 'react';
import { toleranceHint } from '@geogame/shared';
import type { NumericAnswerSpec } from '@geogame/shared';

import type { DigitKey } from '../lib/numeric';
import {
  DIGIT_KEYS,
  backspace,
  displaySlots,
  forcedDigit,
  isSubmittable,
  legalNextDigits,
  pushDigit,
  valueOf,
} from '../lib/numeric';

export interface NumericPadProps {
  readonly spec: NumericAnswerSpec;
  readonly disabled: boolean;
  readonly onSubmit: (value: number) => void;
  /** Bumped by the caller to reset the pad — a new rung, a new answer. */
  readonly resetKey?: string | number;
}

export function NumericPad({
  spec,
  disabled,
  onSubmit,
  resetKey,
}: NumericPadProps): React.JSX.Element {
  const [digits, setDigits] = useState('');

  // A new rung starts empty. Keyed on the caller's token rather than on `spec`
  // identity, because two rungs can legitimately share the same spec object.
  useEffect(() => {
    setDigits('');
  }, [resetKey, spec.quantity]);

  const legal = new Set<string>(legalNextDigits(spec, digits));
  // The one key the bounds leave, if there is one. Lit, never pressed.
  const forced = forcedDigit(spec, digits);
  // What "close" is worth here, said out loud. Null where there is no band.
  const nearHint = toleranceHint(spec);
  const slots = displaySlots(spec, digits);
  const ready = isSubmittable(spec, digits);
  const value = valueOf(spec, digits);

  const tap = (key: DigitKey): void => {
    if (disabled) return;
    setDigits((d) => pushDigit(spec, d, key));
  };

  return (
    <div className="gg-pad" data-disabled={disabled ? 'true' : 'false'}>
      <div className="gg-pad-readout">
        <div className="gg-pad-slots" aria-live="polite">
          {Array.from({ length: slots }, (_, i) => (
            <span key={i} className="gg-pad-slot" data-filled={digits[i] ? 'true' : 'false'}>
              {digits[i] ?? ''}
            </span>
          ))}
        </div>
        <span className="gg-pad-unit">{spec.unit}</span>
      </div>
      <p className="gg-pad-hint">{spec.hint}</p>
      {nearHint === null ? null : <p className="gg-pad-hint gg-pad-hint-near">{nearHint}</p>}

      {/*
        Dialler layout exactly: 1-9 in three rows, then clear / 0 / backspace.
        The zero keeps its dialler position rather than being appended, because
        a thumb that has typed a phone number this decade will go there first.
      */}
      <div className="gg-pad-keys">
        {DIGIT_KEYS.filter((k) => k !== '0').map((key) => (
          <button
            key={key}
            type="button"
            className="gg-pad-key"
            // A key that cannot begin a legal answer is not a choice the player
            // has; showing it enabled would invite a tap that does nothing.
            disabled={disabled || !legal.has(key)}
            data-forced={!disabled && forced === key ? 'true' : 'false'}
            onClick={() => tap(key)}
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          className="gg-pad-key gg-pad-key-edit"
          disabled={disabled || digits === ''}
          onClick={() => setDigits('')}
        >
          clear
        </button>
        <button
          type="button"
          className="gg-pad-key"
          disabled={disabled || !legal.has('0')}
          data-forced={!disabled && forced === '0' ? 'true' : 'false'}
          onClick={() => tap('0')}
        >
          0
        </button>
        <button
          type="button"
          className="gg-pad-key gg-pad-key-edit"
          disabled={disabled || digits === ''}
          onClick={() => setDigits((d) => backspace(d))}
          aria-label="Delete last digit"
        >
          ⌫
        </button>
      </div>

      <button
        type="button"
        className="gg-pad-commit"
        data-ready={ready ? 'true' : 'false'}
        disabled={disabled || !ready}
        onClick={() => {
          if (value !== null) onSubmit(value);
        }}
      >
        {ready ? `Lock in ${value}` : 'Keep tapping…'}
      </button>
    </div>
  );
}
